import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_CONTAINER_SX,
  FIT_TABLE_SX,
  FitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

interface StageCell {
  amount_eur: number | null;
  date: string | null;
  tx_id: number | null;
  reference: string | null;
  detail: string | null;
  status: string | null;
  calc_month: string | null;
  match_score: number | null;
  source?: string | null;
  source_label?: string | null;
  txs?: Array<{
    amount_eur: number | null;
    date: string | null;
    tx_id: number | null;
    reference: string | null;
    source?: string | null;
    source_label?: string | null;
  }> | null;
  share?: {
    kind: string | null;
    balanced: boolean;
    total_eur: number | null;
    members: Array<{
      booking_id: number;
      guest_name: string | null;
      property: string | null;
      check_in_date: string | null;
      amount_eur: number | null;
    }>;
  } | null;
}

interface BookingLedgerRow {
  booking_id: number;
  check_in_date: string | null;
  check_out_date: string | null;
  property: string | null;
  guest_name: string | null;
  platform: string | null;
  external_booking_id: string | null;
  status: string | null;
  invoice_month: string | null;
  invoice_amount_eur: number | null;
  invoice_paid_tx_id: number | null;
  invoice_paid_amount_eur?: number | null;
  invoice_paid_date?: string | null;
  stages: {
    booking: StageCell | null;
    remittance: StageCell | null;
    hosting_fee: StageCell | null;
  };
  ledger_status: {
    id: string;
    label: string;
  };
}

interface OtherPayment {
  type: string;
  type_label: string;
  date: string | null;
  amount_eur: number | null;
  tx_id: number | null;
  reference: string | null;
  detail: string | null;
  calc_month?: string | null;
  invoice_id?: string | null;
  invoice_due_eur?: number | null;
  tx_description?: string | null;
  covers_from?: string | null;
  covers_to?: string | null;
  covers_label?: string | null;
}

interface LedgerSummary {
  niall_account_bookings: number;
  remittances: number;
  remittances_matched_to_booking: number;
  remittances_unmatched: number;
  bookings_without_matched_remittance: number;
  hosting_fee_payments: number;
  expense_payments_to_niall: number;
  fee_corrections: number;
  remittances_returned?: number;
  sum_booking_host_payout_eur: number;
  sum_remittances_eur: number;
  sum_hosting_fees_paid_eur: number;
  sum_stay_hosting_fees_eur?: number;
}

interface LedgerResponse {
  since: string | null;
  generated_at: string;
  layout?: string;
  stage_columns?: { id: string; label: string }[];
  ledger_status_options?: { id: string; label: string }[];
  summary: LedgerSummary;
  rows: BookingLedgerRow[];
  other_payments?: OtherPayment[];
}

type RemittanceFilter = 'all' | 'received' | 'missing';
type HostingFeeFilter = 'all' | 'has' | 'missing';
type SortKey =
  | 'booking_id'
  | 'check_in_date'
  | 'property'
  | 'platform'
  | 'guest_name'
  | 'booking_amount'
  | 'remittance_amount'
  | 'hosting_fee_amount'
  | 'invoice_month'
  | 'invoice_paid_tx_id'
  | 'ledger_status';
type SortOrder = 'asc' | 'desc';

export const LEDGER_COL_WIDTHS = [
  { key: 'checkIn', pct: 9 },
  { key: 'id', pct: 5 },
  { key: 'property', pct: 6 },
  { key: 'platform', pct: 6 },
  { key: 'guest', pct: 11 },
  { key: 'booking', pct: 10 },
  { key: 'remittance', pct: 11 },
  { key: 'hostingFee', pct: 8 },
  { key: 'invoice', pct: 7 },
  { key: 'invoicePaid', pct: 11 },
  { key: 'status', pct: 16 },
] as const;

export const OTHER_PAYMENT_COL_WIDTHS = [
  { key: 'date', pct: 16 },
  { key: 'detail', pct: 48 },
  { key: 'amount', pct: 18 },
  { key: 'ref', pct: 18 },
] as const;

export const HOSTING_FEE_INVOICE_COL_WIDTHS = [
  { key: 'paymentDate', pct: 12 },
  { key: 'invoiceMonth', pct: 12 },
  { key: 'invoiceId', pct: 13 },
  { key: 'amountDue', pct: 13 },
  { key: 'amountPaid', pct: 13 },
  { key: 'txId', pct: 10 },
  { key: 'txDescription', pct: 27 },
] as const;

export const EXPENSE_COL_WIDTHS = [
  { key: 'paymentDate', pct: 14 },
  { key: 'dateRange', pct: 22 },
  { key: 'detail', pct: 32 },
  { key: 'amount', pct: 16 },
  { key: 'ref', pct: 16 },
] as const;

export const COLUMN_HEADER_TOOLTIPS: Record<SortKey, string> = {
  check_in_date: 'Guest check-in date. Check-out is shown underneath.',
  booking_id: 'Internal booking ID from the Bookings ledger.',
  property: 'Property nickname for this stay.',
  platform: 'Booking channel (VRBO, Airbnb, Booking.com, …).',
  guest_name:
    'Guest name. External reservation code (for example HA-…) is shown underneath when we have it.',
  booking_amount:
    'Host payout for this stay — what Niall should remit. Amount, payout date, and Revolut TX id when linked.',
  remittance_amount:
    'Matched remittance Niall sent us for this stay: amount, payment date, and Revolut TX id or AIB account (for pre-Revolut bank-statement credits). A stay paid in several TXs (for example a €10k EFT split) lists each payment; a credit that covers several stays is labelled Shared.',
  hosting_fee_amount:
    'This stay’s share of Niall’s hosting fee (incl. VAT) from the calc sheet — not the full monthly invoice.',
  invoice_month:
    'The monthly hosting invoice that includes this stay: month and the full invoice total (incl. VAT).',
  invoice_paid_tx_id:
    'Payment that settled that monthly invoice: amount, payment date, and Revolut TX id (same layout as Remittance from Niall). A dash means no matching payment was found.',
  ledger_status:
    'Pipeline status: Complete = remittance at the booking amount and invoice paid; otherwise remittance missing/mismatch or invoice unpaid/unknown.',
};

export const OTHER_PAYMENT_HEADER_TOOLTIPS = {
  date: 'Posted date of this Niall-related Revolut payment.',
  detail: 'What this payment is (invoice period, expense description, unmatched remittance, or correction).',
  amount: 'Signed amount in EUR. Credits from Niall are positive; payments to Niall are negative.',
  ref: 'Revolut TX id and bank reference, when available.',
  paymentDate: 'Date this Revolut payment was made.',
  invoiceMonth: 'Calendar month this invoice covers (from Niall’s calc sheet or the payment reference).',
  invoiceId: 'Supplier invoice number (for example INV-0027) from the Revolut payment reference.',
  amountDue: 'Invoice total due from Niall’s calc sheet (incl. VAT when recorded).',
  amountPaid: 'Amount actually paid on this Revolut transfer (negative = money out).',
  txId: 'Internal Revolut transaction id.',
  txDescription: 'Revolut payment description (who it was sent to).',
  expenseDateRange:
    'Months this expense claim covers: the previous payment’s “up to” month through this payment’s “up to” month.',
};

const HEADER_WRAP_SX = {
  whiteSpace: 'pre-line' as const,
  overflowWrap: 'break-word' as const,
  wordBreak: 'normal' as const,
  lineHeight: 1.15,
};

function HeaderTip({ label, tip }: { label: string; tip: string }) {
  return (
    <Tooltip
      title={tip}
      arrow
      enterDelay={0}
      placement="top"
      slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
    >
      <Box
        component="span"
        tabIndex={0}
        data-testid="niall-header-tip"
        sx={{
          ...HEADER_WRAP_SX,
          display: 'block',
          width: '100%',
          minWidth: 0,
          textAlign: 'center',
          textDecoration: 'underline dotted',
          textUnderlineOffset: '2px',
          cursor: 'help',
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

const DEFAULT_STATUS_OPTIONS = [
  { id: 'complete', label: 'Complete' },
  { id: 'remittance_missing', label: 'Niall not sent remittance' },
  { id: 'remittance_mismatch', label: 'Remittance amount mismatch' },
  { id: 'fee_unpaid', label: 'Niall not paid' },
  { id: 'fee_unknown', label: 'Hosting fee unknown' },
];

const OTHER_PAYMENT_SECTIONS: {
  id: string;
  title: string;
  description: string;
  types: string[];
}[] = [
  {
    id: 'hosting_fee',
    title: 'Monthly hosting fee invoices',
    description:
      'Payments to Niall / YourCityCohost for monthly hosting invoices (not tied to a single booking row).',
    types: ['hosting_fee'],
  },
  {
    id: 'expense',
    title: 'Expenses',
    description: 'Expense reimbursements paid to Niall (separate from hosting fees).',
    types: ['expense'],
  },
  {
    id: 'fee_correction',
    title: 'Misdirected client funds',
    description:
      'Wrong-account hosting outs to Niall and the matching returned amounts (correct re-pays to YourCityCohost stay under monthly invoices).',
    types: ['fee_correction'],
  },
  {
    id: 'remittance_unmatched',
    title: 'Unmatched remittances',
    description:
      'Credits from Niall that look like guest remittances but could not be matched to a booking.',
    types: ['remittance_unmatched'],
  },
  {
    id: 'remittance_returned',
    title: 'Returned remittances',
    description:
      'Credits from Niall that were sent in error and paid back (they are not unmatched leftover remittances).',
    types: ['remittance_returned'],
  },
];

function money(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n < 0 ? '−' : '';
  return `${sign}€${Math.abs(n).toLocaleString('en-IE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function invoiceMonthOf(row: BookingLedgerRow): string | null {
  return row.invoice_month || row.stages.hosting_fee?.calc_month || null;
}

function invoicePaidTxOf(row: BookingLedgerRow): number | null {
  return row.invoice_paid_tx_id ?? row.stages.hosting_fee?.tx_id ?? null;
}

function invoicePaidStageOf(
  row: BookingLedgerRow,
  otherPayments?: OtherPayment[]
): StageCell | null {
  const txId = invoicePaidTxOf(row);
  let amount = row.invoice_paid_amount_eur ?? null;
  let date = row.invoice_paid_date || row.stages.hosting_fee?.date || null;
  if (txId != null && (amount == null || !date) && otherPayments?.length) {
    const pay = otherPayments.find((p) => p.tx_id === txId);
    if (pay) {
      if (amount == null) amount = pay.amount_eur;
      if (!date) date = pay.date;
    }
  }
  if (txId == null && amount == null && !date) return null;
  return {
    amount_eur: amount,
    date,
    tx_id: txId,
    reference: null,
    detail: null,
    status: null,
    calc_month: invoiceMonthOf(row),
    match_score: null,
  };
}

function invoiceAmountOf(row: BookingLedgerRow): number | null {
  return row.invoice_amount_eur ?? null;
}

function formatInvoiceMonth(ym: string | null | undefined): string {
  if (!ym) return '—';
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const idx = Number(m[2]) - 1;
  if (idx < 0 || idx > 11) return ym;
  return `${months[idx]} ${m[1]}`;
}

function StayFeeAmount({ stage }: { stage: StageCell | null | undefined }) {
  if (!stage || stage.amount_eur == null) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Typography variant="body2" fontWeight={600} sx={{ color: 'error.main' }}>
      {money(stage.amount_eur)}
    </Typography>
  );
}

function paymentCaption(line: {
  tx_id?: number | null;
  source_label?: string | null;
}): string | null {
  if (line.source_label) {
    return line.source_label;
  }
  if (line.tx_id != null) {
    return `tx #${line.tx_id}`;
  }
  return null;
}

function StageAmount({
  stage,
  tone,
}: {
  stage: StageCell | null | undefined;
  tone?: 'success' | 'error' | 'info';
}) {
  if (!stage || (stage.amount_eur == null && !stage.date && stage.tx_id == null)) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  const color =
    tone === 'success'
      ? 'success.main'
      : tone === 'error'
        ? 'error.main'
        : tone === 'info'
          ? 'info.main'
          : 'text.primary';
  const txLines =
    stage.txs && stage.txs.length > 0
      ? stage.txs
      : [
          {
            amount_eur: stage.amount_eur,
            date: stage.date,
            tx_id: stage.tx_id,
            reference: stage.reference,
            source: stage.source,
            source_label: stage.source_label,
          },
        ];
  const shareMembers = stage.share?.members || [];
  const shared = shareMembers.length > 1;
  const split = txLines.length > 1;
  const shareTip = shared
    ? shareMembers
        .map(
          (m) =>
            `${m.guest_name || '—'} · ${m.property || '—'} · ${money(m.amount_eur)}`
        )
        .join('\n')
    : '';
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ color }}>
        {money(stage.amount_eur)}
      </Typography>
      {shared ? (
        <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{shareTip}</Box>} arrow>
          <Chip
            size="small"
            label={`Shared · ${shareMembers.length}`}
            sx={{ height: 18, my: 0.25, '& .MuiChip-label': { px: 0.6, fontSize: '0.65rem' } }}
          />
        </Tooltip>
      ) : null}
      {split
        ? txLines.map((line) => (
            <Box key={line.tx_id ?? `${line.date}-${line.amount_eur}`}>
              {line.amount_eur != null && line.amount_eur !== stage.amount_eur ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {money(line.amount_eur)}
                </Typography>
              ) : null}
              {line.date ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {line.date}
                </Typography>
              ) : null}
              {paymentCaption(line) ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {paymentCaption(line)}
                </Typography>
              ) : null}
            </Box>
          ))
        : (
            <>
              {stage.date ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {stage.date}
                </Typography>
              ) : null}
              {paymentCaption(stage) ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {paymentCaption(stage)}
                </Typography>
              ) : null}
            </>
          )}
    </Box>
  );
}

function ledgerStatusChipColor(
  id: string
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (id) {
    case 'complete':
      return 'success';
    case 'remittance_missing':
      return 'error';
    case 'remittance_mismatch':
      return 'warning';
    case 'fee_unpaid':
      return 'warning';
    case 'fee_unknown':
      return 'info';
    default:
      return 'default';
  }
}

function sumStage(
  rows: BookingLedgerRow[],
  key: 'booking' | 'remittance' | 'hosting_fee'
): number {
  return rows.reduce((acc, r) => {
    const amt = r.stages[key]?.amount_eur;
    return acc + (typeof amt === 'number' && Number.isFinite(amt) ? amt : 0);
  }, 0);
}

function sortValue(row: BookingLedgerRow, key: SortKey): string | number {
  switch (key) {
    case 'booking_id':
      return row.booking_id;
    case 'check_in_date':
      return row.check_in_date || '';
    case 'property':
      return (row.property || '').toLowerCase();
    case 'platform':
      return (row.platform || '').toLowerCase();
    case 'guest_name':
      return (row.guest_name || '').toLowerCase();
    case 'booking_amount':
      return row.stages.booking?.amount_eur ?? Number.NEGATIVE_INFINITY;
    case 'remittance_amount':
      return row.stages.remittance?.amount_eur ?? Number.NEGATIVE_INFINITY;
    case 'hosting_fee_amount':
      return row.stages.hosting_fee?.amount_eur ?? Number.NEGATIVE_INFINITY;
    case 'invoice_month':
      return invoiceMonthOf(row) || '';
    case 'invoice_paid_tx_id':
      return invoicePaidTxOf(row) ?? Number.NEGATIVE_INFINITY;
    case 'ledger_status':
      return (row.ledger_status?.label || '').toLowerCase();
    default:
      return '';
  }
}

function compareRows(a: BookingLedgerRow, b: BookingLedgerRow, key: SortKey, order: SortOrder): number {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  let cmp = 0;
  if (typeof av === 'number' && typeof bv === 'number') {
    cmp = av === bv ? 0 : av < bv ? -1 : 1;
  } else {
    cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  }
  if (cmp === 0) {
    // Stable tie-break: newer check-in, then higher booking id
    const dateCmp = (b.check_in_date || '').localeCompare(a.check_in_date || '');
    if (dateCmp !== 0) return dateCmp;
    return b.booking_id - a.booking_id;
  }
  return order === 'asc' ? cmp : -cmp;
}

export const NiallAccountLedger: React.FC = () => {
  const [data, setData] = useState<LedgerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [checkInFrom, setCheckInFrom] = useState('');
  const [checkInTo, setCheckInTo] = useState('');
  const [properties, setProperties] = useState<string[]>([]);
  const [guestQuery, setGuestQuery] = useState('');
  const [platform, setPlatform] = useState('');
  const [remittanceFilter, setRemittanceFilter] = useState<RemittanceFilter>('all');
  const [hostingFeeFilter, setHostingFeeFilter] = useState<HostingFeeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortKey>('check_in_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [otherExpanded, setOtherExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // All-time ledger (omit since)
        const res = await api.get('/api/niall-calcs/account-ledger');
        if (!cancelled) {
          setData(res.data);
          setError('');
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || 'Failed to load Niall account ledger');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const propertyOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of data?.rows || []) {
      if (r.property) set.add(r.property);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const platformOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of data?.rows || []) {
      if (r.platform) set.add(r.platform);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const statusOptions = data?.ledger_status_options?.length
    ? data.ledger_status_options
    : DEFAULT_STATUS_OPTIONS;

  const hasActiveFilters =
    !!checkInFrom ||
    !!checkInTo ||
    properties.length > 0 ||
    !!guestQuery.trim() ||
    !!platform ||
    remittanceFilter !== 'all' ||
    hostingFeeFilter !== 'all' ||
    statusFilter !== 'all';

  const clearFilters = () => {
    setCheckInFrom('');
    setCheckInTo('');
    setProperties([]);
    setGuestQuery('');
    setPlatform('');
    setRemittanceFilter('all');
    setHostingFeeFilter('all');
    setStatusFilter('all');
  };

  const filteredRows = useMemo(() => {
    const guest = guestQuery.trim().toLowerCase();
    return (data?.rows || []).filter((r) => {
      if (checkInFrom && (r.check_in_date || '') < checkInFrom) return false;
      if (checkInTo && (r.check_in_date || '') > checkInTo) return false;
      if (properties.length > 0 && !properties.includes(r.property || '')) return false;
      if (platform && r.platform !== platform) return false;
      if (guest) {
        const hay = `${r.guest_name || ''} ${r.external_booking_id || ''}`.toLowerCase();
        if (!hay.includes(guest)) return false;
      }
      const hasRemit = Boolean(r.stages.remittance?.amount_eur != null || r.stages.remittance?.tx_id);
      if (remittanceFilter === 'received' && !hasRemit) return false;
      if (remittanceFilter === 'missing' && hasRemit) return false;
      const hasFee = Boolean(r.stages.hosting_fee?.amount_eur != null);
      if (hostingFeeFilter === 'has' && !hasFee) return false;
      if (hostingFeeFilter === 'missing' && hasFee) return false;
      if (statusFilter !== 'all' && r.ledger_status?.id !== statusFilter) return false;
      return true;
    });
  }, [
    data,
    checkInFrom,
    checkInTo,
    properties,
    guestQuery,
    platform,
    remittanceFilter,
    hostingFeeFilter,
    statusFilter,
  ]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((a, b) => compareRows(a, b, sortBy, sortOrder));
    return rows;
  }, [filteredRows, sortBy, sortOrder]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    // Amount / date columns: first click = newest / largest first
    const preferDescFirst =
      key === 'check_in_date' ||
      key === 'booking_id' ||
      key === 'booking_amount' ||
      key === 'remittance_amount' ||
      key === 'hosting_fee_amount' ||
      key === 'invoice_month' ||
      key === 'invoice_paid_tx_id';
    setSortBy(key);
    setSortOrder(preferDescFirst ? 'desc' : 'asc');
  };
  const filteredOtherPayments = useMemo(() => {
    return (data?.other_payments || []).filter((p) => {
      if (checkInFrom && (p.date || '') < checkInFrom) return false;
      if (checkInTo && (p.date || '') > checkInTo) return false;
      return true;
    });
  }, [data, checkInFrom, checkInTo]);

  const otherSectionsWithRows = useMemo(() => {
    return OTHER_PAYMENT_SECTIONS.map((section) => ({
      ...section,
      rows: filteredOtherPayments.filter((p) => section.types.includes(p.type)),
    })).filter((section) => section.rows.length > 0);
  }, [filteredOtherPayments]);

  // Keep accordion expand state in sync when visible sections change; new sections start open.
  useEffect(() => {
    setOtherExpanded((prev) => {
      const next: Record<string, boolean> = {};
      for (const section of otherSectionsWithRows) {
        next[section.id] = prev[section.id] ?? true;
      }
      return next;
    });
  }, [otherSectionsWithRows]);

  const allOtherOpen =
    otherSectionsWithRows.length > 0 &&
    otherSectionsWithRows.every((s) => otherExpanded[s.id]);
  const allOtherClosed =
    otherSectionsWithRows.length > 0 &&
    otherSectionsWithRows.every((s) => otherExpanded[s.id] === false);

  const openAllOtherSections = () => {
    setOtherExpanded(
      Object.fromEntries(otherSectionsWithRows.map((s) => [s.id, true]))
    );
  };
  const closeAllOtherSections = () => {
    setOtherExpanded(
      Object.fromEntries(otherSectionsWithRows.map((s) => [s.id, false]))
    );
  };

  const filteredSummary = useMemo(() => {
    const withRemit = filteredRows.filter(
      (r) => r.stages.remittance?.amount_eur != null || r.stages.remittance?.tx_id
    ).length;
    return {
      bookings: filteredRows.length,
      sumBooking: sumStage(filteredRows, 'booking'),
      sumRemit: sumStage(filteredRows, 'remittance'),
      sumStayFees: sumStage(filteredRows, 'hosting_fee'),
      withRemit,
      withoutRemit: filteredRows.length - withRemit,
    };
  }, [filteredRows]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError('')}>
        {error}
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Niall account ledger
        </Typography>
        <Typography variant="body2" color="text.secondary">
          One row per Niall-account booking (all time
          {data.rows.length ? ` · ${data.rows.length} stays` : ''}). Columns are pipeline stages:
          booking host payout, remittance from Niall, and hosting fee from Niall’s calc sheet.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            useFlexGap
            flexWrap="wrap"
            alignItems={{ md: 'center' }}
          >
            <TextField
              label="Check-in from"
              type="date"
              size="small"
              value={checkInFrom}
              onChange={(e) => setCheckInFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="Check-in to"
              type="date"
              size="small"
              value={checkInTo}
              onChange={(e) => setCheckInTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <Autocomplete
              multiple
              size="small"
              options={propertyOptions}
              value={properties}
              onChange={(_, v) => setProperties(v)}
              renderInput={(params) => <TextField {...params} label="Property" />}
              sx={{ minWidth: 220, flex: 1 }}
            />
            <TextField
              label="Guest / ref"
              size="small"
              value={guestQuery}
              onChange={(e) => setGuestQuery(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="niall-ledger-platform">Platform</InputLabel>
              <Select
                labelId="niall-ledger-platform"
                label="Platform"
                value={platform}
                onChange={(e) => setPlatform(String(e.target.value))}
              >
                <MenuItem value="">All</MenuItem>
                {platformOptions.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="niall-ledger-remit">Remittance</InputLabel>
              <Select
                labelId="niall-ledger-remit"
                label="Remittance"
                value={remittanceFilter}
                onChange={(e) => setRemittanceFilter(e.target.value as RemittanceFilter)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="received">Received</MenuItem>
                <MenuItem value="missing">Missing</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="niall-ledger-fee">Hosting fee</InputLabel>
              <Select
                labelId="niall-ledger-fee"
                label="Hosting fee"
                value={hostingFeeFilter}
                onChange={(e) => setHostingFeeFilter(e.target.value as HostingFeeFilter)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="has">Has fee</MenuItem>
                <MenuItem value="missing">No fee</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="niall-ledger-status">Status</InputLabel>
              <Select
                labelId="niall-ledger-status"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(String(e.target.value))}
              >
                <MenuItem value="all">All</MenuItem>
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </Button>
          </Stack>
          {hasActiveFilters ? (
            <Typography variant="caption" color="text.secondary">
              Showing {filteredRows.length} of {data.rows.length} bookings
              {filteredOtherPayments.length !== (data.other_payments || []).length
                ? ` · ${filteredOtherPayments.length} of ${(data.other_payments || []).length} other payments`
                : ''}
            </Typography>
          ) : null}
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
        <Chip
          color="info"
          label={`${filteredSummary.bookings} bookings · ${money(filteredSummary.sumBooking)}`}
        />
        <Chip
          color="success"
          label={`${filteredSummary.withRemit} remitted · ${money(filteredSummary.sumRemit)}`}
        />
        <Chip color="error" label={`Stay fees ${money(filteredSummary.sumStayFees)}`} />
        <Chip variant="outlined" label={`${filteredSummary.withoutRemit} without remittance`} />
      </Stack>

      <Alert severity="info">
        Status tags: <strong>Complete</strong> = remittance received at the booking amount and the
        monthly hosting invoice covering that stay is paid; <strong>Niall not sent remittance</strong>{' '}
        = no matched remittance; <strong>Niall not paid</strong> = remittance received but hosting
        invoice not paid yet; <strong>Remittance amount mismatch</strong> /{' '}
        <strong>Hosting fee unknown</strong> cover the remaining cases.
      </Alert>

      <Typography variant="caption" color="text.secondary">
        Generated {data.generated_at}
      </Typography>

      <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
        <Table size="small" stickyHeader sx={FIT_TABLE_SX} data-testid="niall-account-ledger-table">
          <FitColgroup cols={[...LEDGER_COL_WIDTHS]} />
          <TableHead>
            <TableRow>
              {(
                [
                  { key: 'check_in_date', label: 'Check-in', align: 'left' as const },
                  { key: 'booking_id', label: 'ID', align: 'right' as const },
                  { key: 'property', label: 'Property', align: 'left' as const },
                  { key: 'platform', label: 'Platform', align: 'left' as const },
                  { key: 'guest_name', label: 'Guest', align: 'left' as const },
                  { key: 'booking_amount', label: 'Booking', align: 'right' as const },
                  { key: 'remittance_amount', label: 'Remittance\nfrom Niall', align: 'right' as const },
                  { key: 'hosting_fee_amount', label: 'Hosting\nfee', align: 'right' as const },
                  { key: 'invoice_month', label: 'Invoice\nmonth', align: 'right' as const },
                  { key: 'invoice_paid_tx_id', label: 'Invoice\npaid?', align: 'right' as const },
                  { key: 'ledger_status', label: 'Status', align: 'left' as const },
                ] as const
              ).map((col) => {
                const defaultDir =
                  col.key === 'property' ||
                  col.key === 'platform' ||
                  col.key === 'guest_name' ||
                  col.key === 'ledger_status'
                    ? 'asc'
                    : 'desc';
                return (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    sortDirection={sortBy === col.key ? sortOrder : false}
                    sx={{
                      ...HEADER_WRAP_SX,
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === col.key}
                      direction={sortBy === col.key ? sortOrder : defaultDir}
                      onClick={() => handleSort(col.key)}
                      sx={{
                        ...HEADER_WRAP_SX,
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        height: 'auto',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        '& .MuiTableSortLabel-icon': { margin: 0, flexShrink: 0 },
                      }}
                    >
                      <HeaderTip label={col.label} tip={COLUMN_HEADER_TOOLTIPS[col.key]} />
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No bookings match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((r) => (
                <TableRow
                  key={r.booking_id}
                  sx={
                    r.stages.remittance
                      ? undefined
                      : { bgcolor: (theme) => theme.palette.action.hover }
                  }
                >
                  <TableCell>
                    <Typography variant="body2">{r.check_in_date || '—'}</Typography>
                    {r.check_out_date ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        → {r.check_out_date}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{r.booking_id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={fitTruncateSx} title={r.property || undefined}>
                      {r.property || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={fitTruncateSx} title={r.platform || undefined}>
                      {r.platform || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={fitTruncateSx} title={r.guest_name || undefined}>
                      {r.guest_name || '—'}
                    </Typography>
                    {r.external_booking_id ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={fitTruncateSx}
                        title={r.external_booking_id}
                      >
                        {r.external_booking_id}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <StageAmount stage={r.stages.booking} tone="info" />
                  </TableCell>
                  <TableCell align="right">
                    <StageAmount stage={r.stages.remittance} tone="success" />
                  </TableCell>
                  <TableCell align="right">
                    <StayFeeAmount stage={r.stages.hosting_fee} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    {invoiceMonthOf(r) || invoiceAmountOf(r) != null ? (
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'normal' }}>
                          {formatInvoiceMonth(invoiceMonthOf(r))}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ whiteSpace: 'normal' }}
                        >
                          {money(invoiceAmountOf(r))}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    <StageAmount
                      stage={invoicePaidStageOf(r, data.other_payments)}
                      tone="error"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={ledgerStatusChipColor(r.ledger_status?.id || '')}
                      label={r.ledger_status?.label || '—'}
                      title={r.ledger_status?.label || undefined}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {otherSectionsWithRows.length > 0 ? (
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1">Other Niall payments</Typography>
              <Typography variant="body2" color="text.secondary">
                Items that are not shown as a stage on a booking row above.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UnfoldMoreIcon />}
                onClick={openAllOtherSections}
                disabled={allOtherOpen}
              >
                Open all
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UnfoldLessIcon />}
                onClick={closeAllOtherSections}
                disabled={allOtherClosed}
              >
                Close all
              </Button>
            </Stack>
          </Stack>

          {otherSectionsWithRows.map((section) => {
            const sectionTotal = section.rows.reduce(
              (acc, p) => acc + (typeof p.amount_eur === 'number' ? p.amount_eur : 0),
              0
            );
            const expanded = otherExpanded[section.id] ?? true;
            return (
              <Accordion
                key={section.id}
                disableGutters
                expanded={expanded}
                onChange={(_, isExpanded) =>
                  setOtherExpanded((prev) => ({ ...prev, [section.id]: isExpanded }))
                }
                variant="outlined"
                data-testid={`niall-other-payments-${section.id}`}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    sx={{ width: '100%', pr: 1 }}
                  >
                    <Box>
                      <Typography variant="subtitle2">{section.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {section.description}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${section.rows.length} · ${money(sectionTotal)}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                    <Table size="small" sx={FIT_TABLE_SX}>
                      {section.id === 'hosting_fee' ? (
                        <>
                          <FitColgroup cols={[...HOSTING_FEE_INVOICE_COL_WIDTHS]} />
                          <TableHead>
                            <TableRow>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip
                                  label={'Payment\ndate'}
                                  tip={OTHER_PAYMENT_HEADER_TOOLTIPS.paymentDate}
                                />
                              </TableCell>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip
                                  label={'Invoice\nmonth'}
                                  tip={OTHER_PAYMENT_HEADER_TOOLTIPS.invoiceMonth}
                                />
                              </TableCell>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip
                                  label={'Invoice\nID'}
                                  tip={OTHER_PAYMENT_HEADER_TOOLTIPS.invoiceId}
                                />
                              </TableCell>
                              <TableCell align="right" sx={HEADER_WRAP_SX}>
                                <HeaderTip
                                  label={'Amount\ndue'}
                                  tip={OTHER_PAYMENT_HEADER_TOOLTIPS.amountDue}
                                />
                              </TableCell>
                              <TableCell align="right" sx={HEADER_WRAP_SX}>
                                <HeaderTip
                                  label={'Amount\npaid'}
                                  tip={OTHER_PAYMENT_HEADER_TOOLTIPS.amountPaid}
                                />
                              </TableCell>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip
                                  label="TX ID"
                                  tip={OTHER_PAYMENT_HEADER_TOOLTIPS.txId}
                                />
                              </TableCell>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip
                                  label={'TX\ndescription'}
                                  tip={OTHER_PAYMENT_HEADER_TOOLTIPS.txDescription}
                                />
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {section.rows.map((p) => (
                              <TableRow key={`${p.type}-${p.tx_id}`}>
                                <TableCell>{p.date || '—'}</TableCell>
                                <TableCell>{formatInvoiceMonth(p.calc_month)}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={fitTruncateSx} title={p.invoice_id || undefined}>
                                    {p.invoice_id || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2">{money(p.invoice_due_eur)}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{
                                      color:
                                        (p.amount_eur ?? 0) >= 0 ? 'success.main' : 'error.main',
                                    }}
                                  >
                                    {money(p.amount_eur)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {p.tx_id != null ? `tx #${p.tx_id}` : '—'}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={fitTruncateSx}
                                    title={p.tx_description || p.reference || undefined}
                                  >
                                    {p.tx_description || p.reference || '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </>
                      ) : section.id === 'expense' ? (
                        <>
                          <FitColgroup cols={[...EXPENSE_COL_WIDTHS]} />
                          <TableHead>
                            <TableRow>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip
                                  label={'Payment\ndate'}
                                  tip={OTHER_PAYMENT_HEADER_TOOLTIPS.paymentDate}
                                />
                              </TableCell>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip
                                  label={'Date\nrange'}
                                  tip={OTHER_PAYMENT_HEADER_TOOLTIPS.expenseDateRange}
                                />
                              </TableCell>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip label="Detail" tip={OTHER_PAYMENT_HEADER_TOOLTIPS.detail} />
                              </TableCell>
                              <TableCell align="right" sx={HEADER_WRAP_SX}>
                                <HeaderTip label="Amount" tip={OTHER_PAYMENT_HEADER_TOOLTIPS.amount} />
                              </TableCell>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip label={'Ref /\ntx'} tip={OTHER_PAYMENT_HEADER_TOOLTIPS.ref} />
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {section.rows.map((p) => (
                              <TableRow key={`${p.type}-${p.tx_id}`}>
                                <TableCell>{p.date || '—'}</TableCell>
                                <TableCell>{p.covers_label || '—'}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={fitTruncateSx}
                                    title={p.detail || undefined}
                                  >
                                    {p.detail || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{
                                      color:
                                        (p.amount_eur ?? 0) >= 0 ? 'success.main' : 'error.main',
                                    }}
                                  >
                                    {money(p.amount_eur)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={fitTruncateSx}
                                  >
                                    {[p.tx_id != null ? `tx #${p.tx_id}` : null, p.reference]
                                      .filter(Boolean)
                                      .join(' · ') || '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </>
                      ) : (
                        <>
                          <FitColgroup cols={[...OTHER_PAYMENT_COL_WIDTHS]} />
                          <TableHead>
                            <TableRow>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip label="Date" tip={OTHER_PAYMENT_HEADER_TOOLTIPS.date} />
                              </TableCell>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip label="Detail" tip={OTHER_PAYMENT_HEADER_TOOLTIPS.detail} />
                              </TableCell>
                              <TableCell align="right" sx={HEADER_WRAP_SX}>
                                <HeaderTip label="Amount" tip={OTHER_PAYMENT_HEADER_TOOLTIPS.amount} />
                              </TableCell>
                              <TableCell sx={HEADER_WRAP_SX}>
                                <HeaderTip label={'Ref /\ntx'} tip={OTHER_PAYMENT_HEADER_TOOLTIPS.ref} />
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {section.rows.map((p) => (
                              <TableRow key={`${p.type}-${p.tx_id}`}>
                                <TableCell>{p.date || '—'}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={fitTruncateSx}
                                    title={p.detail || undefined}
                                  >
                                    {p.detail || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{
                                      color:
                                        (p.amount_eur ?? 0) >= 0 ? 'success.main' : 'error.main',
                                    }}
                                  >
                                    {money(p.amount_eur)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={fitTruncateSx}
                                  >
                                    {[p.tx_id != null ? `tx #${p.tx_id}` : null, p.reference]
                                      .filter(Boolean)
                                      .join(' · ') || '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </>
                      )}
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default NiallAccountLedger;
